import { useRef } from "react";
import { Control, Path, useController } from "react-hook-form";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { usePagination } from "hooks";

import { misc } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import { SALE_CANCELED_STATUS_TYPE } from "constants/sales";
import { ApplyClaimFormValues } from "features/insurance/claim-remittance-modal/apply-claim-form.component";
import useScroll from "hooks/use-scroll.hook";
import { useGetPatientSalesQuery } from "services/sales";
import { PatientSalesFilters, Sale } from "services/types/sales.type";

import DisplaySaleListItem from "./display-sale-list-item.component";
import { getSaleDisplay } from "./util";

type PatientSalesSelectListProps<T extends ApplyClaimFormValues> = {
  control: Control<T, object>;
  patientId?: number;
  excludedStatuses?: number[];
  title?: string;
  showSelected?: boolean;
  onlyWithClaims?: boolean;
  highlightTotal?: string;
  highlightCptCodes?: string[];
  sx?: SxProps;
};

const defaultExcludedStatuses = [SALE_CANCELED_STATUS_TYPE];

export default function PatientSalesSelectList<
  T extends Partial<ApplyClaimFormValues>,
>({
  control,
  patientId,
  title = "Please select invoice",
  showSelected,
  excludedStatuses = defaultExcludedStatuses,
  onlyWithClaims,
  highlightTotal,
  highlightCptCodes = [],
  sx
}: PatientSalesSelectListProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    data: sales,
    isLoading,
    fetchMore,
    hasMoreData
  } = usePagination<PatientSalesFilters, Sale>({
    useQuery: useGetPatientSalesQuery,
    limit: 25,
    params: {
      patient: patientId!,
      excluded_status: excludedStatuses
    },
    options: {
      skip: !patientId
    }
  });
  const triggerRef = useScroll({
    containerRef,
    isLoading,
    hasMoreData,
    fetchMore
  });
  const { field } = useController({
    name: "sale" as Path<T>,
    control,
    rules: { required: true }
  });
  const { value, onChange, onBlur } = field;

  return (
    <Box
      component={Paper}
      sx={{
        height: "100%",
        maxHeight: "50vh",
        overflowY: "auto",
        overflowX: "hidden",
        ...sx
      }}
      ref={containerRef}
      data-testid="patient-sales-select-list"
    >
      {Boolean(title) && (
        <Box
          sx={{
            pl: 2,
            pt: 1,
            pb: 1,
            background:
              showSelected && Boolean(field.value) ? misc.lightGreen : undefined
          }}
        >
          <Typography
            fontSize={20}
            lineHeight={1.3}
            fontWeight={700}
            component="div"
            pb={0.25}
            pt={0.5}
          >
            {showSelected && Boolean(field.value)
              ? `Selected Invoice: ${getSaleDisplay(field.value)}`
              : title}
          </Typography>
        </Box>
      )}
      <List sx={{ pt: 0 }}>
        {sales?.map((sale, index) => (
          <DisplaySaleListItem
            index={index}
            key={sale.id}
            sale={sale}
            selected={value?.id === sale.id}
            onClick={() => {
              onChange(sale);
              onBlur();
            }}
            onlyWithClaims={onlyWithClaims}
            highlightTotal={highlightTotal}
            highlightCptCodes={highlightCptCodes}
          />
        ))}
        {!isLoading && sales?.length === 0 && (
          <ListItem disableGutters data-testid="row-no-data-found">
            <ListItemButton>
              <ListItemText primary="No patient sales found" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      {isLoading && <CircularProgress sx={{ width: "100%" }} />}
      <Box
        data-testid="fetch-more-patients-trigger"
        ref={triggerRef}
        sx={{ height: "16px" }}
      />
    </Box>
  );
}
